import React from "react";
import {Drawer, List, ListItem, ListItemIcon, ListItemText, Divider, Toolbar, Box} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Agriculture as FarmIcon,
  Water as PondIcon,
  Inventory as FeedIcon,
  People as UserIcon,
  Settings as SettingsIcon,
  Business as TenantIcon,
  Help as HelpIcon,
  Analytics as AnalyticsIcon,
  Science as ScienceIcon,
  HowToReg as AccessRequestIcon,
  PriceCheck as PriceCheckIcon,
} from "@mui/icons-material";
import {Link as RouterLink, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useAuth} from "../../contexts/AuthContext";
import {UserRole} from "../../api/user";

const drawerWidth = 240;

interface SidebarProps {
  isSidebarOpen: boolean;
  handleDrawerToggle: () => void;
  isMobile: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({isSidebarOpen, handleDrawerToggle, isMobile}) => {
  const {t} = useTranslation();
  const {user} = useAuth();
  const location = useLocation();

  const mainNavItems = [
    {text: t("Dashboard"), icon: <DashboardIcon />, path: "/dashboard"},
    {text: t("Farms"), icon: <FarmIcon />, path: "/farms"},
    {text: t("Ponds"), icon: <PondIcon />, path: "/ponds"},
    {text: t("Predictive Analytics"), icon: <AnalyticsIcon />, path: "/predictive"},
    {text: t("Advanced Analytics"), icon: <ScienceIcon />, path: "/advanced-predictive"},
    {text: t("Market Prices"), icon: <PriceCheckIcon />, path: "/market-prices"},
  ];

  const superAdminNavItems = [{text: t("Template Management"), icon: <FeedIcon />, path: "/template-management", roles: [UserRole.SUPER_ADMIN]}];

  const adminNavItems = [
    {text: t("Tenants"), icon: <TenantIcon />, path: "/tenants", roles: [UserRole.SUPER_ADMIN]},
    {text: t("Access Requests"), icon: <AccessRequestIcon />, path: "/access-requests", roles: [UserRole.SUPER_ADMIN]},
    {text: t("Users"), icon: <UserIcon />, path: "/users", roles: [UserRole.SUPER_ADMIN, UserRole.TENANT_ADMIN, UserRole.FARMER_ADMIN]},
    {text: t("Configuration"), icon: <SettingsIcon />, path: "/config", roles: [UserRole.SUPER_ADMIN, UserRole.TENANT_ADMIN, UserRole.FARMER_ADMIN]},
  ];

  const supportNavItems = [{text: t("Help"), icon: <HelpIcon />, path: "/help"}];

  const renderNavItems = (items: typeof mainNavItems) => (
    <List>
      {items.map((item) => {
        const isActive = location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path));
        return (
          <ListItem
            button
            component={RouterLink}
            to={item.path}
            key={item.text}
            selected={isActive}
            sx={{
              "&.Mui-selected": {
                backgroundColor: "rgba(25, 118, 210, 0.08)",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.12)",
                },
              },
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
              borderRadius: 1,
              mx: 1,
              my: 0.5,
            }}>
            <ListItemIcon sx={{minWidth: 40, color: isActive ? "primary.main" : "inherit"}}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: isActive ? "medium" : "regular",
                color: isActive ? "primary" : "text.primary",
              }}
            />
          </ListItem>
        );
      })}
    </List>
  );

  const renderAdminNavItems = (items: typeof adminNavItems) => (
    <List>
      {items.map((item) => {
        // Check if user has required role
        if (item.roles && !item.roles.includes(user?.role as UserRole)) {
          return null;
        }

        const isActive = location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path));
        return (
          <ListItem
            button
            component={RouterLink}
            to={item.path}
            key={item.text}
            selected={isActive}
            sx={{
              "&.Mui-selected": {
                backgroundColor: "rgba(25, 118, 210, 0.08)",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.12)",
                },
              },
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
              borderRadius: 1,
              mx: 1,
              my: 0.5,
            }}>
            <ListItemIcon sx={{minWidth: 40, color: isActive ? "primary.main" : "inherit"}}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: isActive ? "medium" : "regular",
                color: isActive ? "primary" : "text.primary",
              }}
            />
          </ListItem>
        );
      })}
    </List>
  );

  const renderSupportNavItems = (items: typeof supportNavItems) => (
    <List>
      {items.map((item) => {
        const isActive = location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path));
        return (
          <ListItem
            button
            component={RouterLink}
            to={item.path}
            key={item.text}
            selected={isActive}
            sx={{
              "&.Mui-selected": {
                backgroundColor: "rgba(25, 118, 210, 0.08)",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.12)",
                },
              },
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
              borderRadius: 1,
              mx: 1,
              my: 0.5,
            }}>
            <ListItemIcon sx={{minWidth: 40, color: isActive ? "primary.main" : "inherit"}}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: isActive ? "medium" : "regular",
                color: isActive ? "primary" : "text.primary",
              }}
            />
          </ListItem>
        );
      })}
    </List>
  );

  const drawerContent = (
    <>
      <Toolbar /> {/* For proper spacing below AppBar */}
      <Box sx={{overflow: "auto", pt: 2}}>
        {renderNavItems(mainNavItems)}
        <Divider sx={{my: 2, mx: 2}} />
        {renderAdminNavItems(superAdminNavItems)}
        <Divider sx={{my: 2, mx: 2}} />
        {renderAdminNavItems(adminNavItems)}
        <Divider sx={{my: 2, mx: 2}} />
        {renderSupportNavItems(supportNavItems)}
      </Box>
    </>
  );

  return (
    <Box component="nav" sx={{width: {md: drawerWidth}, flexShrink: {md: 0}}}>
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={isSidebarOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: {xs: "block", md: "none"},
            "& .MuiDrawer-paper": {boxSizing: "border-box", width: drawerWidth},
          }}>
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            display: {xs: "none", md: "block"},
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              borderRight: "none",
              boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.05), 0px 4px 5px 0px rgba(0,0,0,0.04)",
            },
          }}
          open>
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
};

export default Sidebar;
